// Generic imports
import _ from 'lodash';
import countryCodes from 'country-codes-list';

// Local imports
import AIRPORTS from '../../airports-parsed.json';
import GoogleComponents from '../../Google.js';

// Cache for lookup maps to avoid rebuilding them
// Using a Map with string keys since WeakMap requires object references
const lookupCache = new Map();

// Build lookup maps for O(1) access instead of O(n) searches
function buildLookupMaps(seaPorts, unlocode) {
    // Create a cache key based on array lengths and first/last items
    // This is a simple heuristic - for exact matching, we'd need to store references
    const cacheKey = `${seaPorts?.length || 0}_${unlocode?.length || 0}_${seaPorts?.[0]?.code || ''}_${unlocode?.[0]?.code || ''}`;
    
    if (lookupCache.has(cacheKey)) {
        return lookupCache.get(cacheKey);
    }

    // Build seaPorts lookup maps
    const seaPortsByCode = new Map();
    const seaPortsByCodePrefix = new Map(); // For partial matches
    const seaPortsByName = new Map();
    
    if (seaPorts && Array.isArray(seaPorts)) {
        for (const port of seaPorts) {
            if (port.code) {
                seaPortsByCode.set(port.code, port);
                // Store by 2-character country prefix for faster country-based searches
                const countryPrefix = port.code.substr(0, 2);
                if (!seaPortsByCodePrefix.has(countryPrefix)) {
                    seaPortsByCodePrefix.set(countryPrefix, []);
                }
                seaPortsByCodePrefix.get(countryPrefix).push(port);
            }
            if (port.portname) {
                const key = port.portname.toUpperCase();
                if (!seaPortsByName.has(key)) {
                    seaPortsByName.set(key, []);
                }
                seaPortsByName.get(key).push(port);
            }
        }
    }

    // Build unlocode lookup maps
    const unlocodeByCode = new Map();
    const unlocodeByText = new Map();
    const unlocodeByCountryCode = new Map();
    
    if (unlocode && Array.isArray(unlocode)) {
        for (const loc of unlocode) {
            if (loc.code) {
                const codeKey = loc.code.toUpperCase();
                unlocodeByCode.set(codeKey, loc);
                
                const countryPrefix = loc.code.substr(0, 2);
                if (!unlocodeByCountryCode.has(countryPrefix)) {
                    unlocodeByCountryCode.set(countryPrefix, []);
                }
                unlocodeByCountryCode.get(countryPrefix).push(loc);
            }
            if (loc.text) {
                const textKey = loc.text.toUpperCase();
                if (!unlocodeByText.has(textKey)) {
                    unlocodeByText.set(textKey, []);
                }
                unlocodeByText.get(textKey).push(loc);
            }
        }
    }

    const maps = {
        seaPortsByCode,
        seaPortsByCodePrefix,
        seaPortsByName,
        unlocodeByCode,
        unlocodeByText,
        unlocodeByCountryCode
    };
    
    lookupCache.set(cacheKey, maps);
    
    // Limit cache size to prevent memory issues
    if (lookupCache.size > 10) {
        const firstKey = lookupCache.keys().next().value;
        lookupCache.delete(firstKey);
    }
    
    return maps;
}

async function settleRoute({ route, country, unlocode, seaPorts, transportationMethod }) {
    if (!route) return { result: null, resultType: null };
    
    const countryCode = country ? countryCodes.findOne('countryNameEn', country) : null;
    let result = null;
    let resultType = 'port';

    // Build lookup maps for efficient searching
    const maps = buildLookupMaps(seaPorts, unlocode);
    const routeUpper = route.toUpperCase();

    if (transportationMethod === 'air') {
        // For air, search in AIRPORTS
        result = AIRPORTS.find(p => p.iata_code === route || p.name.toUpperCase() === routeUpper);
    } else {
        // For sea/land, search in seaPorts
        // First try exact code match
        result = maps.seaPortsByCode.get(route);
        
        // Then try partial code match (route includes port code)
        if (!result && seaPorts && Array.isArray(seaPorts)) {
            for (const [code, port] of maps.seaPortsByCode) {
                if (route.includes(code)) {
                    result = port;
                    break;
                }
            }
        }
        
        // Then try country-based search with portname match
        if (!result && countryCode?.countryCode) {
            const countryPorts = maps.seaPortsByCodePrefix.get(countryCode.countryCode) || [];
            result = countryPorts.find(p => routeUpper.includes(p.portname?.toUpperCase()));
        }
    }

    // If not found in ports, try unlocode
    if (!result && country) {
        // Try exact code match
        result = maps.unlocodeByCode.get(routeUpper);
        
        // Try text match with country filter
        if (!result && countryCode?.countryCode) {
            const countryUnlocodes = maps.unlocodeByCountryCode.get(countryCode.countryCode) || [];
            result = countryUnlocodes.find(u => u.text?.toUpperCase() === routeUpper);
        }
        
        if (result) {
            resultType = 'unlocode';
        }
    }

    // If still not found and it's a 5-character sea route, create a simple port object
    if (transportationMethod === 'sea' && !result && route.length === 5) {
        result = {
            code: route,
            portname: route,
        };
        resultType = 'port';
    }

    // Last resort: try Google Maps geocoding for longer routes
    if (!result && route && route.length > 6) {
        result = await resolveGmaps(route);
        resultType = 'geocode';
    }

    return { result, resultType };
}

// Simpler versions of an address, most specific first. An inquiry's address often carries a
// company name, a building or floor, or a placeholder ("Col. Otra No Especificada En El
// Catálogo"), and Google then finds nothing at all for the whole string; the AI wizard left such
// routes empty ("Any → Any"). The versions drop those parts, then the street, then all but the last
// two parts (city or postcode, country).
export function addressCandidates(input) {
    const raw = String(input || '').trim();
    const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
    const noise = /\b(co\.?|ltd\.?|limited|inc\.?|llc|gmbh|s\.?\s?a\.?(\s?de\s?c\.?\s?v\.?)?|s\.?r\.?l\.?|b\.?v\.?|corp\.?|company|industry|industries|logistics|services)\b|\(|\)|no especificad|\botra\b|n\/a|not specified|\b(building|bldg|floor|fl\.|room|rm\.?|suite|unit|piso|edificio|nave|bodega)\b/i;
    const building = /\b(building|bldg|floor|room|suite|unit|piso|edificio)\b/i;
    const clean = parts.filter(p => !noise.test(p) || (/^\d/.test(p) && !building.test(p)));
    const out = [raw, clean.join(', ')];
    for (let i = 1; i < clean.length - 1; i++) out.push(clean.slice(i).join(', '));
    if (clean.length >= 2) out.push(clean.slice(-2).join(', '));
    return [...new Set(out.filter(Boolean))];
}

async function resolveGmaps(input) {
    try {
        if (!GoogleComponents.autocomplete) {
            return null;
        }
        // The address as given may match a business too; a simplified one must match a place,
        // or it lands on whatever business shares a word with it.
        const candidates = addressCandidates(input);
        for (let i = 0; i < candidates.length; i++) {
            const request = { types: i === 0 ? ['geocode', 'establishment'] : ['geocode'], input: candidates[i] };
            const suggestions = await GoogleComponents.autocomplete.getPlacePredictions(request).catch(() => null);
            const result = _.first(suggestions?.predictions);
            if (result) {
                return await getGmapsSuggestionValue(result);
            }
        }
        return null;
    } catch (e) {
        return null;
    }
}

async function getGmapsSuggestionValue(suggestion) {
    const request = {
        placeId: suggestion.place_id,
        fields: ['name', 'address_components', 'types']
    };

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    return new Promise((res) => {
        service.getDetails(request, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                const type = (() => {
                    if (place.types.includes('country')) return 'country';
                    if (place.types.includes('locality')) return 'city';
                    return 'geocode';
                })();
                const countryCode = place.address_components.find(ac => ac.types.includes('country')).short_name;
                const city = (place.address_components.find(ac => ac.types.includes('locality')) || {}).short_name;
                res({ text: suggestion.description, countryCode, city, type, id: suggestion.place_id });
            } else {
                res(null);
            }
        });
    });
}

export { settleRoute };